import { useCallback, useContext, useEffect, useMemo } from "react";

import { create } from "@bufbuild/protobuf";
import { useQueryClient } from "@tanstack/react-query";

import { SourceSyncStatus } from "@gx/ts-contracts/services/database/models/public_pb";
import { DeleteSourceRequestSchema } from "@gx/ts-contracts/services/source/api/source_pb";
import { GetWebhookSourceBySourceIdRequestSchema } from "@gx/ts-contracts/services/webhook/api/webhook_pb";

import { CREATE_SOURCE_UNEXPECTED_ERROR } from "@/pages/sources/components/create/sources/constants";
import {
  getCreateSourceNameError,
  isCreateSourceNameProvided,
} from "@/pages/sources/components/create/sources/utils";
import { CreateSourceWebhookActionType } from "@/pages/sources/components/create/sources/webhook/actions";
import { CREATE_SOURCE_WEBHOOK_FAILED_TO_CREATE_ERROR_HEADER } from "@/pages/sources/components/create/sources/webhook/constants";
import {
  CreateSourceWebhookContext,
  CreateSourceWebhookContextShape,
} from "@/pages/sources/components/create/sources/webhook/CreateSourceWebhookProvider";
import { CreateSourceWebhookPhase } from "@/pages/sources/components/create/sources/webhook/types";
import {
  buildCreateSourceWebhookRequest,
  extractWebhookProvisioningResult,
  isWebhookInProgress,
} from "@/pages/sources/components/create/sources/webhook/utils";

import { ToastVariant } from "@/providers/toast/Toast";
import { useToast } from "@/providers/toast/useToast";

import { useCreateSourceMutation, useDeleteSourceMutation } from "@/api/mutations/sources";
import { useGetWebhookSourceBySourceIdMutation } from "@/api/mutations/webhook-sources";
import { createListSourcesInfiniteQueryKey } from "@/api/queries/sources";

export function useCreateSourceWebhookContext(): CreateSourceWebhookContextShape {
  const context = useContext(CreateSourceWebhookContext);
  if (!context) {
    throw new Error(
      "useCreateSourceWebhookContext must be used within a CreateSourceWebhookProvider",
    );
  }
  return context;
}

interface UseCreateSourceWebhookOptions {
  onSuccess?: (sourceId: string) => void;
}

export function useCreateSourceWebhook({ onSuccess }: UseCreateSourceWebhookOptions) {
  const { state, dispatch } = useCreateSourceWebhookContext();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { mutateAsync: createSourceMutation, isPending: isCreating } = useCreateSourceMutation();
  const { mutateAsync: deleteSourceMutation, isPending: isDeleting } = useDeleteSourceMutation();
  const { mutateAsync: getWebhookSourceMutation } = useGetWebhookSourceBySourceIdMutation();

  const isDisabled = useMemo(() => isWebhookInProgress(state.phase), [state.phase]);
  const nameError = useMemo(() => getCreateSourceNameError(state), [state]);

  // Auto-poll for webhook test when in LISTENING phase
  useEffect(() => {
    if (state.phase !== CreateSourceWebhookPhase.LISTENING || !state.sourceId) {
      return;
    }

    const sourceId = state.sourceId;

    const pollInterval = setInterval(() => {
      void (async () => {
        try {
          const response = await getWebhookSourceMutation(
            create(GetWebhookSourceBySourceIdRequestSchema, { sourceId }),
          );

          const syncStatus = response.webhookSource?.syncStatus ?? SourceSyncStatus.UNSPECIFIED;

          if (syncStatus === SourceSyncStatus.COMPLETE) {
            dispatch({
              type: CreateSourceWebhookActionType.SET_PHASE,
              payload: CreateSourceWebhookPhase.CONFIRMED,
            });
            showToast({
              variant: ToastVariant.SUCCESS,
              header: "Webhook test received",
              subheader: "Your webhook source is active.",
            });
          }
        } catch {
          // Silently ignore polling errors
        }
      })();
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [state.phase, state.sourceId, dispatch, getWebhookSourceMutation, showToast]);

  const createWebhook = useCallback(async () => {
    dispatch({ type: CreateSourceWebhookActionType.SET_SHOULD_SHOW_ERRORS, payload: true });

    if (!isCreateSourceNameProvided(state)) {
      return;
    }

    try {
      dispatch({
        type: CreateSourceWebhookActionType.SET_PHASE,
        payload: CreateSourceWebhookPhase.CREATING,
      });

      const createRequest = buildCreateSourceWebhookRequest(state);
      const createSourceResponse = await createSourceMutation(createRequest);
      const provisioningResult = extractWebhookProvisioningResult(createSourceResponse);

      dispatch({
        type: CreateSourceWebhookActionType.SET_SOURCE_ID,
        payload: provisioningResult.sourceId,
      });
      dispatch({
        type: CreateSourceWebhookActionType.SET_CREDENTIALS,
        payload: provisioningResult.credentials,
      });
      dispatch({
        type: CreateSourceWebhookActionType.SET_PHASE,
        payload: CreateSourceWebhookPhase.LISTENING,
      });

      void queryClient.invalidateQueries({ queryKey: createListSourcesInfiniteQueryKey() });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : CREATE_SOURCE_UNEXPECTED_ERROR;
      dispatch({ type: CreateSourceWebhookActionType.SET_ERROR, payload: errorMessage });
      showToast({
        variant: ToastVariant.ERROR,
        header: CREATE_SOURCE_WEBHOOK_FAILED_TO_CREATE_ERROR_HEADER,
        subheader: errorMessage,
      });
    }
  }, [state, dispatch, createSourceMutation, queryClient, showToast]);

  const handleDone = useCallback(() => {
    if (onSuccess && state.sourceId) {
      onSuccess(state.sourceId);
    }
  }, [onSuccess, state.sourceId]);

  const handleDeleteWebhookSource = useCallback(async () => {
    if (!state.sourceId) return;
    await deleteSourceMutation(create(DeleteSourceRequestSchema, { sourceId: state.sourceId }));
  }, [state.sourceId, deleteSourceMutation]);

  return {
    state,
    dispatch,
    createWebhook,
    handleDone,
    handleDeleteWebhookSource,
    isDisabled,
    nameError,
    isCreating,
    isDeleting,
  };
}
